import axios from 'axios';
import { userPool } from '../authentication/AuthContext';

const API_BASE_URL = process.env.REACT_APP_RESEARCH_API_URL;

const isAwsApi = API_BASE_URL.includes("amazonaws.com");

function parseApiResponse(response) {
  if (isAwsApi) {
    // AWS integration returns the data more directly
    return response.data.data;
  } else {
    // Local integration requires parsing JSON again
    const bodyData = JSON.parse(response.data.body);
    return bodyData.data;
  }
}

// Helper function to get the JWT token
const getAuthToken = async () => {
  const user = userPool.getCurrentUser();
  if (!user) {
    console.error('No user found in userPool');
    throw new Error('No user logged in');
  }

  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        // console.error('Session error:', err);
        reject(new Error('Authentication failed'));
      } else {
        resolve(session.getIdToken().getJwtToken());
      }
    });
  });
};

// Create an authenticated axios instance
const createAuthenticatedRequest = async (method, url, data = null) => {
  try {
    const token = await getAuthToken();
    
    const config = {
      method,
      url,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    };
    
    if (data) {
      config.data = data;
    }


    
    return axios(config);
  } catch (error) {
    // console.error('Authentication error:', error);
    throw error;
  }
};

export const api = {
  getPreviousResearches: async (userID) => {
    console.log("load previous researches")
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/research/${userID}`);
      const result = parseApiResponse(response);
      console.log("previous researches result ", result);
      return result;

    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  deleteResearch: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/remove/${userID}/${researchID}`);
      console.log('API response:', response.data);
      return response.data;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getPreviousResearch: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/research/${userID}/${researchID}`);
      const result = parseApiResponse(response);
      console.log("get previous survey result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  // MARK-THIS
  createSurvey: async (userID, surveyDescription, researchType = 'general') => {
    const knownInfo = ["age range", "education", "employment status", "gender", "household situation", "income", "marital status"];


    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/create`, {
        userID,
        surveyDescription,
        researchType,
        knownInfo
      });
      const resultCreate = parseApiResponse(response);
      console.log("create survey ", resultCreate);
      return resultCreate;

    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  updateSurvey: async (userID, researchID, questions_list, surveyContext = '') => {
    try {
      const questions = questions_list.map(question => ({
        questionID: question.questionID,
        prompt: question.questionText,
        kind: question.questionType,
        vocab: question.responseOptions,
        max_length: question.maxLength,
        max: question.selectMax,
        min: question.selectMin,
        responsePercentages: question.responsePercentages,
        hasFixedPercentages: question.hasFixedPercentages,
      }));

      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/update_questions`, {
        userID,
        researchID,
        questions,
        surveyContext
      });
      const result = parseApiResponse(response);
      console.log("update survey result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  addContext: async (userID, researchID, surveyContext) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/update_survey_context`, {
        userID,
        researchID,
        surveyContext
      });
      const result = parseApiResponse(response);
      console.log("add context result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  selectPersonas: async (params) => {
    if (!params.userID || !params.researchID) {
      throw new Error('User ID and Research ID are required');
    }
    try {
      const requestBody = {
        userID: params.userID,
        researchID: params.researchID,
        numberOfPersonas: parseInt(params.numberOfPersonas),
        gender: params.gender,
        ageRange: params.ageRange,
        employmentStatus: params.employmentStatus,
        income: params.income,
        education: params.education,
        householdSituation: params.householdSituation,
        maritalStatus: params.maritalStatus,
      };
      
      console.log('Sending persona selection request:', requestBody);
      
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/personas/create`, requestBody);
      const result = parseApiResponse(response);
      console.log("create personas result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getPreviousPersonas: async (userID, researchID) => {
    if (!userID || !researchID) {
      throw new Error('User ID and Research ID are required');
    }
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/personas/${userID}/${researchID}`);
      const result = parseApiResponse(response);
      console.log("previous personas result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getAllPreviousPersonas: async (userID) => {
    if (!userID) {
      throw new Error('User ID is required');
    }
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/personas/${userID}`);
      const result = parseApiResponse(response);
      console.log("all previous personas result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getExistingResponses: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/responses/${userID}/${researchID}`);
      const result = parseApiResponse(response);
      console.log("existing responses result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getPreviousResponseData: async (userID) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/responses/${userID}`);
      const result = parseApiResponse(response);
      console.log("all previous responses result ", result);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },
  
  generateResponses: async (userID, researchID) => {
    await createAuthenticatedRequest('post', `${API_BASE_URL}/responses/create`, {
      userID,
      researchID
    });
  },

  generateQuestionResponses: async (userID, researchID, questionID) => {
    await createAuthenticatedRequest('post', `${API_BASE_URL}/responses/update_question`, {
      userID,
      researchID,
      questionID
    });
  },

  getDashboardStats: async () => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/dashboard/stats`);
      return response.data;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getDashboardData: async (dataType, filterField = null, filterValue = null) => {
    try {
      let url = `${API_BASE_URL}/dashboard/data/${dataType}`;
      if (filterField && filterValue) {
        url += `?filter_field=${filterField}&filter_value=${filterValue}`;
      }
      const response = await createAuthenticatedRequest('get', url);
      return response.data;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  deleteDashboardRecord: async (dataType, id) => {
    try {
      const response = await createAuthenticatedRequest('delete', `${API_BASE_URL}/dashboard/data/${dataType}/${id}`);
      return response.data;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  generateAnalysis: async (userID, researchID, researchQuestion) => {

    try {
      const requestBody = {
        userID,
        researchID,
        researchQuestion
      };

      const response = await createAuthenticatedRequest(
        'post',
        `${API_BASE_URL}/analysis/survey/generate`,
        requestBody
      );

      console.log('API response (generateAnalysis):', response.data);

      const result = parseApiResponse(response);
      console.log("Analysis generation result", result);
      return result;
    } catch (error) {
      // console.error('API Error (generateAnalysis):', error.response?.data || error.message);
      throw error;
    }
  },

  getSavedAnalysis: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest(
        'get',
        `${API_BASE_URL}/analysis/${userID}/${researchID}/survey`
      );
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error (getSavedAnalysis):', error.response?.data || error.message);
      throw error;
    }
  },


  generateResponsesAnalysis: async (
    userID,
    researchID,
    researchQuestion,
    totalCounts,
    groupedData,
    questionID
  ) => {
    try {
      const requestBody = {
        userID,
        researchID,
        researchQuestion,
        totalCounts,
        groupedData,
        questionID
      };

      const response = await createAuthenticatedRequest(
        'post',
        `${API_BASE_URL}/analysis/responses/generate`,
        requestBody
      );

      // Now use your parseApiResponse helper for consistent parsing
      const result = parseApiResponse(response);
      console.log("Responses analysis result:", result);
      return result;
    } catch (error) {
      // console.error('Error generating responses analysis:', error.response?.data || error.message);
      throw error;
    }
  },

  getSavedQuestionAnalyses: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest(
        'get',
        `${API_BASE_URL}/analysis/${userID}/${researchID}/questions`
      );
      const result = parseApiResponse(response);
      console.log("Saved question analyses:", result);
      return result;
    } catch (error) {
      // console.error('Error fetching saved question analyses:', error.response?.data || error.message);
      throw error;
    }
  },

  getRemainingAttempts: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest(
        'get',
        `${API_BASE_URL}/analysis/${userID}/${researchID}/attempts`
      );
      const result = parseApiResponse(response);
      console.log("Remaining attempts:", result);
      return result;
    } catch (error) {
      // console.error('Error fetching remaining attempts:', error.response?.data || error.message);
      throw error;
    }
  },
  
  decrementAttempt: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest(
        'post',
        `${API_BASE_URL}/analysis/${userID}/${researchID}/decrement`
      );
      const result = parseApiResponse(response);
      console.log("Attempts decremented:", result);
      return result;
    } catch (error) {
      // console.error('Error decrementing attempts:', error.response?.data || error.message);
      throw error;
    }
  },

  duplicateResearch: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/research/duplicate/${userID}/${researchID}`);
      const result = parseApiResponse(response);
      console.log("duplicate survey ", result);
      return result;

    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  // Poll APIs
  getAllPolls: async () => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/polls`);
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getPoll: async (pollId) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/polls/${pollId}`);
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  createPoll: async (data) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/polls/create`, data);
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  deletePoll: async (pollId) => {
    try {
      const response = await createAuthenticatedRequest('delete', `${API_BASE_URL}/polls/${pollId}`);
      const result = parseApiResponse(response);
      return result;
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  generateFullAnalysis: async (userID, researchID, researchQuestion, questions) => {
    try {
      const response = await createAuthenticatedRequest('post', `${API_BASE_URL}/analysis/full/generate`, {
        userID,
        researchID,
        researchQuestion,
        questions
      });
      return parseApiResponse(response);
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  getAnalysisStatus: async (userID, researchID) => {
    try {
      const response = await createAuthenticatedRequest('get', `${API_BASE_URL}/analysis/${userID}/${researchID}/status`);
      return parseApiResponse(response);
    } catch (error) {
      // console.error('API Error:', error.response?.data || error.message);
      throw error;
    }
  },

  waitForAnalysisCompletion: async (userID, researchID) => {
    const POLL_INTERVAL = 2000; // Poll every 2 seconds
    const MAX_ATTEMPTS = 60; // Maximum 2 minutes of polling
    let attempts = 0;

    while (attempts < MAX_ATTEMPTS) {
      const status = await api.getAnalysisStatus(userID, researchID);
      
      switch (status.status) {
        case 'COMPLETED':
          return status;
        case 'ERROR':
          throw new Error(status.error_msg || 'Analysis failed');
        case 'PENDING':
          // Wait before polling again
          await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
          attempts++;
          continue;
        default:
          throw new Error(`Unknown status: ${status.status}`);
      }
    }

    throw new Error('Analysis timed out');
  },
};

